import {
  type Client,
  createRemoteClient,
  makeEcosystemMixedOpStore,
  StaticOpStore,
  Weave,
} from '@wandb/weave/core';
import React, {useMemo} from 'react';

import {
  ComputeGraphContextProviderFromClient,
  getGlobalWeaveClient,
} from './clientContext';
import {Spec as PanelEach} from './components/Panel2/PanelEach';
import {Spec as PanelEachColumn} from './components/Panel2/PanelEachColumn';
import {Spec as PanelExpr} from './components/Panel2/PanelExpr';
import {Spec as PanelFacet} from './components/Panel2/PanelFacet';
// import {Spec as PanelFacetTabs} from './components/Panel2/PanelFacetTabs';
import {Spec as PanelGroup} from './components/Panel2/PanelGroup';
import {Spec as PanelSelectEditor} from './components/Panel2/PanelSelectEditor';
// import {Spec as PanelSections} from './components/Panel2/PanelSections';
import {Spec as PanelSlider} from './components/Panel2/PanelSlider';
import {
  RowSize,
  TABLE_CONFIG_DEFAULTS,
} from './components/Panel2/PanelTable/config';
import {useLoadWeaveObjects} from './components/Panel2/remoteOpStoreLoader';
import getConfig from './config';
import {type WeaveFeatures, WeaveFeaturesContext} from './context';

export const getGlobalWeaveContext = () => {
  return new Weave(getGlobalWeaveClient());
};

export {ComputeGraphContextProviderFromClient} from './clientContext';

const useRemoteEcosystemClient = (
  isAdmin: boolean = false,
  tokenFunc: () => Promise<string | undefined>
) => {
  const {loading, remoteOpStore} = useLoadWeaveObjects();
  return useMemo(() => {
    if (loading || remoteOpStore == null) {
      return null;
    }
    return createRemoteClient(
      getConfig().backendWeaveExecutionUrl(),
      tokenFunc,
      isAdmin,
      makeEcosystemMixedOpStore(StaticOpStore.getInstance(), remoteOpStore),
      (input, init) => window.fetch(input as any, init)
    );
  }, [isAdmin, remoteOpStore, tokenFunc, loading]);
};

export const RemoteEcosystemComputeGraphContextProvider: React.FC<{
  children?: React.ReactNode;
  isAdmin?: boolean;
  tokenFunc?: () => Promise<string | undefined>;
}> = React.memo(({isAdmin, tokenFunc, children}) => {
  const tf = useMemo(() => {
    const dummy = async () => undefined;
    return tokenFunc != null ? tokenFunc : dummy;
  }, [tokenFunc]);
  const client = useRemoteEcosystemClient(!!isAdmin, tf);
  if (client == null) {
    return <></>;
  }
  return (
    <ComputeGraphContextProviderFromClient client={client}>
      {children}
    </ComputeGraphContextProviderFromClient>
  );
});
RemoteEcosystemComputeGraphContextProvider.displayName =
  'RemoteEcosystemComputeGraphContextProvider';

const UNHIDE_PANELS = [
  PanelGroup,
  PanelEachColumn,
  PanelExpr,
  PanelFacet,
  // PanelFacetTabs,
  // PanelSections,
  PanelEach,
  PanelSlider,
  PanelSelectEditor,
];

export const NotebookComputeGraphContextProvider: React.FC<{
  children?: React.ReactNode;
}> = React.memo(({children}) => {
  // Default state when in jupyter notebook weave session
  const defaultWBFeatureState: WeaveFeatures = useMemo(
    () => ({
      actions: true,
      fullscreenMode: true,
      clientEvalInUseNodeValueEnabled: true,
      sidebarConfigStylingEnabled: true,
      errorBoundaryInPanelComp2Enabled: true,
      redesignedPlotConfigEnabled: true,
      betaFeatures: {
        'weave-python-ecosystem': true,
        'weave-devpopup': false,
      },
    }),
    []
  );

  // oxlint-disable-next-line react/immutability
  TABLE_CONFIG_DEFAULTS.rowSize = RowSize.Small;
  for (const panel of UNHIDE_PANELS) {
    // oxlint-disable-next-line react/immutability
    panel.hidden = false;
  }

  return (
    <WeaveFeaturesContext.Provider value={defaultWBFeatureState}>
      <RemoteEcosystemComputeGraphContextProvider>
        {children}
      </RemoteEcosystemComputeGraphContextProvider>
    </WeaveFeaturesContext.Provider>
  );
});
NotebookComputeGraphContextProvider.displayName =
  'NotebookComputeGraphContextProvider';
